import { Link } from 'react-router-dom'
import Header from '../components/Header'
import Footer from '../components/Footer'

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl lg:text-5xl font-bold text-vokaris-teal mb-6">
            About Vokaris
          </h1>
          <p className="text-xl text-vokaris-teal/70 max-w-2xl mx-auto">
            Building the infrastructure platform that empowers builders—from solo developers to enterprise teams—to ship faster and scale with confidence.
          </p>
        </div>

        <div className="prose prose-lg max-w-none mb-16">
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">Our Mission</h2>
            <p className="text-vokaris-teal/70 mb-4">
              Vokaris is on a mission to eliminate the infrastructure complexity that slows down software development. We believe builders should focus on creating great products, not wrestling with infrastructure.
            </p>
            <p className="text-vokaris-teal/70">
              Whether you're a solo developer launching your first SaaS, a growing startup scaling fast, or an enterprise modernizing your stack—our platform provides everything you need. From identity and compute to storage and app services, all designed to work together seamlessly so you can ship faster without compromising on security or scalability.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">What We Offer</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-vokaris-surface p-6 rounded-xl border border-gray-100">
                <h3 className="text-lg font-bold text-vokaris-teal mb-2">🔐 Identity & Access</h3>
                <p className="text-vokaris-teal/70 text-sm">
                  Complete authentication and authorization with support for social logins, SSO, and fine-grained access control.
                </p>
              </div>
              <div className="bg-vokaris-surface p-6 rounded-xl border border-gray-100">
                <h3 className="text-lg font-bold text-vokaris-teal mb-2">⚡ Compute at Scale</h3>
                <p className="text-vokaris-teal/70 text-sm">
                  From bare-metal to serverless, run your workloads on infrastructure that scales with your needs.
                </p>
              </div>
              <div className="bg-vokaris-surface p-6 rounded-xl border border-gray-100">
                <h3 className="text-lg font-bold text-vokaris-teal mb-2">💾 Storage & Secrets</h3>
                <p className="text-vokaris-teal/70 text-sm">
                  Secure, scalable storage for your data with built-in encryption and secrets management.
                </p>
              </div>
              <div className="bg-vokaris-surface p-6 rounded-xl border border-gray-100">
                <h3 className="text-lg font-bold text-vokaris-teal mb-2">🚀 Developer SDKs</h3>
                <p className="text-vokaris-teal/70 text-sm">
                  Type-safe SDKs for TypeScript, Python, Go, and more—integrate in minutes, not days.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold text-vokaris-teal mb-4">Why Choose Vokaris?</h2>
            <ul className="space-y-3 text-vokaris-teal/70">
              <li className="flex items-start gap-3">
                <span className="text-vokaris-green text-xl">✓</span>
                <span><strong className="text-vokaris-teal">All-in-One Platform:</strong> Everything you need in one integrated platform—no more juggling multiple vendors or stitching services together.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-vokaris-green text-xl">✓</span>
                <span><strong className="text-vokaris-teal">Builder-First:</strong> Built by developers, for developers. Our APIs and SDKs are intuitive and well-documented—get started in minutes.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-vokaris-green text-xl">✓</span>
                <span><strong className="text-vokaris-teal">Scales With You:</strong> Start free as a solo builder. Scale seamlessly as your team and traffic grow. Enterprise-ready when you need it.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-vokaris-green text-xl">✓</span>
                <span><strong className="text-vokaris-teal">Transparent Pricing:</strong> Pay only for what you use with clear, predictable pricing—no hidden costs or surprise bills.</span>
              </li>
            </ul>
          </section>
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-br from-vokaris-blue to-white p-10 rounded-2xl border border-vokaris-teal/10 text-center">
          <h2 className="text-3xl font-bold text-vokaris-teal mb-4">Get In Touch</h2>
          <p className="text-vokaris-teal/70 mb-6 text-lg">
            Have questions or want to learn more about Vokaris?
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-vokaris-teal/70">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-vokaris-teal rounded-full flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
              <div className="text-left">
                <p className="text-sm text-vokaris-teal/50">Email us at</p>
                <a href="mailto:hello@vokaris.io" className="text-lg font-bold text-vokaris-green hover:text-vokaris-teal transition">
                  hello@vokaris.io
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Back Link */}
        <div className="mt-12 text-center">
          <Link
            to="/"
            className="text-vokaris-green font-semibold hover:text-vokaris-teal transition"
          >
            ← Back to Home
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  )
}
